import React from 'react';
import clsx from 'clsx';
import { CheckIcon, DocumentTextIcon, SparklesIcon } from '@heroicons/react/24/outline';

import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Button } from '@oysterjs/ui/button';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { DataTable, type ColumnDef } from '@oysterjs/ui/data-table';
import { TableHeader } from '@oysterjs/ui/table';
import { Textarea } from '@oysterjs/ui/textarea';
import {
  SearchFilterInput,
  type ActiveFilter,
  type FilterDefinition
} from '@oysterjs/ui/search-filter';
import {
  type CaraWorkspaceFieldsFragment,
  type CaraWorkspacePreset,
  type StartPresetGenerationInput,
  useGetCustomFormsQuery,
  useGetHtmlDocumentTemplatesQuery,
  useGetWordDocumentTemplatesQuery,
  useSearchCaraWorkspacesQuery
} from '~/api/graphql';

import {
  activeFiltersToSearchFilter,
  createFilterDefinitions,
  type WorkspaceFilter
} from '~/api/data/workspaces/copilot-filters';
import { USE_CASE_FAMILIES, familyForPreset } from '~/components/home/use-case-catalog';
import { type CaraAttachment, templateAttachment } from '~/components/cara-input/cara-attachment';
import {
  customFormPickerOptions,
  htmlTemplatePickerOptions,
  wordTemplatePickerOptions
} from '~/components/cara-input/template-picker-options';
import {
  CONVERT_TO_PRESET_STEPS,
  CONVERT_TO_PRESET_STEP_LABELS,
  GENERATION_STEP,
  MAX_CONVERT_TO_PRESET_WORKSPACES,
  type ConvertToPresetStep,
  type ConvertToPresetWizardState,
  type PresetFileOption,
  type PresetGenerationStatus,
  assembleStartPresetGenerationInput,
  canProceedFromStep,
  convertToPresetWizardReducer,
  defaultBasePreset,
  initialConvertToPresetWizardState,
  isAtWorkspaceCapacity,
  isPresetGenerationInFlight
} from '~/components/workspaces/lib/convert-to-preset-wizard.utils';
import { ConvertToPresetCompletion } from './convert-to-preset-completion';
import { ConvertToPresetGenerationStep } from './convert-to-preset-generation-step';
import { ConvertToPresetQuestionsStep } from './convert-to-preset-questions-step';
import { OutputTemplatePicker } from '~/components/cara-input/output-template-picker';
import { PresetFilePicker, PresetFilePreview } from './preset-file-picker';
// Same picker copy the preset editor uses: the shared default speaks in terms of
// the compose box ("your message and attachments"), which has no referent on a
// preset-authoring surface.
import {
  presetCustomFormPickerDescription,
  presetHTMLTemplatePickerDescription,
  presetWordTemplatePickerDescription
} from '~/components/cara-input/preset-picker-descriptions';

// Mirrors maxPresetOutputFormatsRunes in pkg/service/api/api/preset_generation.go:
// exceeding it fails startPresetGeneration at step 5, after every input is entered.
const MAX_OUTPUT_FORMATS_CHARS = 5000;

const basePresetTitle = (preset: CaraWorkspacePreset): string =>
  familyForPreset(preset)?.title ?? preset;

const StepIndicator = ({ currentStep }: { currentStep: ConvertToPresetStep }) => (
  <div className="flex items-center gap-0 mb-5">
    {CONVERT_TO_PRESET_STEPS.map((stepNum, i) => {
      const completed = currentStep > stepNum;
      const current = currentStep === stepNum;
      return (
        <React.Fragment key={stepNum}>
          {i > 0 && (
            <div
              className={clsx(
                'flex-1 h-px mx-2',
                completed ? 'bg-sky-400' : 'bg-neutral-200 dark:bg-neutral-700'
              )}
            />
          )}
          <div className="flex items-center gap-1.5 shrink-0">
            <div
              className={clsx(
                'size-5 rounded-full flex items-center justify-center text-xs font-bold',
                (completed || current) && 'bg-sky-500 text-white',
                !completed &&
                  !current &&
                  'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
              )}
            >
              {completed ? <CheckIcon className="size-3" aria-hidden /> : stepNum}
            </div>
            <span
              className={clsx(
                'text-xs',
                current
                  ? 'text-neutral-700 dark:text-neutral-300 font-medium'
                  : 'text-neutral-400 dark:text-neutral-500'
              )}
            >
              {CONVERT_TO_PRESET_STEP_LABELS[stepNum]}
            </span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

/** Step 1: names what the wizard produces. No inputs — it never blocks Next. */
const OrientationStep = ({ workspaceTitle }: { workspaceTitle: string }) => (
  <div className="space-y-3">
    <p className="text-sm text-neutral-600 dark:text-neutral-400 m-0">
      Turn <span className="font-medium text-neutral-900 dark:text-white">{workspaceTitle}</span>{' '}
      into a reusable custom preset — a saved starting point your team can launch new workspaces
      from.
    </p>
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 p-4 space-y-2">
      <p className="flex items-start gap-2 text-xs text-neutral-600 dark:text-neutral-400 m-0">
        <SparklesIcon className="size-4 text-sky-500 dark:text-sky-400 shrink-0" aria-hidden />
        <span>
          <span className="font-medium text-neutral-900 dark:text-white">Preset</span> —
          instructions and defaults that shape how Cara works. This wizard creates one.
        </span>
      </p>
      <p className="flex items-start gap-2 text-xs text-neutral-600 dark:text-neutral-400 m-0">
        <DocumentTextIcon
          className="size-4 text-neutral-400 dark:text-neutral-500 shrink-0"
          aria-hidden
        />
        <span>
          <span className="font-medium text-neutral-900 dark:text-white">Template</span> — a
          document Cara fills in. Attach templates as preset defaults in step 3.
        </span>
      </p>
    </div>
  </div>
);

// The Time scalar arrives as a string even though codegen types it as Date, so it
// is normalized once here (same as documents-columns' getArtifactCreatedDate).
const toDate = (value: Date | string | null | undefined): Date | null =>
  value ? new Date(value) : null;

// Small enough that the table needs no scroller of its own; a user looking for
// an older workspace narrows with the search field rather than scrolling.
const WORKSPACE_SEARCH_PAGE_SIZE = 10;

/**
 * Step 2's workspace picker. A table rather than a listbox: an agency can have
 * thousands of workspaces, so the choice needs title, creator and date to be
 * distinguishable. The source workspace is pinned and cannot be deselected.
 */
const WorkspacePickerTable = ({
  open,
  source,
  selectedIds,
  onChange
}: {
  open: boolean;
  source: { id: string; title: string };
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) => {
  const filterDefinitions = React.useMemo<FilterDefinition<WorkspaceFilter>[]>(
    () => createFilterDefinitions(),
    []
  );
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilter<WorkspaceFilter>[]>([]);
  const filter = React.useMemo(() => activeFiltersToSearchFilter(activeFilters), [activeFilters]);

  const { data, loading, error } = useSearchCaraWorkspacesQuery({
    variables: { first: WORKSPACE_SEARCH_PAGE_SIZE, filter },
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  type Row = { id: string; title: string; creator: string; createdAt?: Date | null };

  const results = React.useMemo<Row[]>(
    () =>
      (data?.searchCaraWorkspaces?.edges ?? []).map((e) => ({
        id: e.node.id,
        title: e.node.title || 'Untitled workspace',
        creator: e.node.agencyUser?.name || e.node.agencyUser?.email || '—',
        createdAt: toDate(e.node.createdAt)
      })),
    [data?.searchCaraWorkspaces?.edges]
  );

  // Write-through cache: a workspace picked earlier must still render its row
  // after a later search drops it from `results`.
  const rowById = React.useRef<Record<string, Row>>({});
  rowById.current[source.id] = { id: source.id, title: source.title, creator: 'You' };
  for (const r of results) rowById.current[r.id] = r;

  const atCapacity = isAtWorkspaceCapacity(selectedIds);

  // Source first, then current selections, then search results — so selections
  // stay visible and removable across searches.
  const rows = React.useMemo(() => {
    const ids: string[] = [];
    for (const id of [source.id, ...selectedIds, ...results.map((r) => r.id)]) {
      if (!ids.includes(id)) ids.push(id);
    }
    return ids.map((id) => rowById.current[id] ?? { id, title: id, creator: '—' });
  }, [source.id, selectedIds, results]);

  const toggle = React.useCallback(
    (id: string) =>
      onChange(
        selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id]
      ),
    [onChange, selectedIds]
  );

  // Column shape follows getWorkspaceColumns in copilot-columns.tsx so the picker
  // reads as the same table as the workspaces list.
  const columns = React.useMemo<ColumnDef<Row>[]>(
    () => [
      {
        id: 'select',
        cellClassName: 'w-0 py-1.5 px-3',
        renderHeader: () => <TableHeader className="w-0 py-1.5 px-3 text-xs" />,
        renderCell: (row) => {
          const selected = selectedIds.includes(row.id);
          return (
            <Checkbox
              color="sky"
              checked={selected}
              disabled={row.id === source.id || (atCapacity && !selected)}
              onChange={() => toggle(row.id)}
              aria-label={`Select ${row.title}`}
            />
          );
        }
      },
      {
        id: 'title',
        header: 'Workspace',
        cellClassName: 'max-w-xs py-1.5 px-3',
        renderCell: (row) => (
          <span className="block truncate font-medium text-neutral-900 dark:text-white">
            {row.title}
          </span>
        )
      },
      {
        id: 'creator',
        header: 'Created by',
        cellClassName: 'py-1.5 px-3 text-neutral-500 dark:text-neutral-400',
        renderCell: (row) => row.creator
      },
      {
        id: 'createdAt',
        header: 'Created',
        cellClassName: 'py-1.5 px-3 text-neutral-500 dark:text-neutral-400',
        renderCell: (row) => (row.createdAt ? row.createdAt.toLocaleDateString() : '—')
      }
    ],
    [atCapacity, selectedIds, source.id, toggle]
  );

  const remaining = MAX_CONVERT_TO_PRESET_WORKSPACES - selectedIds.length;
  const totalCount = data?.searchCaraWorkspaces?.totalCount ?? 0;

  return (
    <div data-slot="control" className="flex flex-col gap-3">
      {/* Own stacking context above the table: the filter dropdown is z-10
          within the input, as is DataTable's loading overlay, so without this the
          suggestions render under the table. */}
      <div className="relative z-20">
        <SearchFilterInput<WorkspaceFilter>
          filterDefinitions={filterDefinitions}
          onFiltersChange={setActiveFilters}
          placeholder="Search workspaces by title, client, status, or preset..."
        />
      </div>

      {/* px-4 cancels the bleeding table's -mx-[--gutter] so the ring still
          frames it, and the gutter is what pads the checkbox column. */}
      <div className="rounded-lg px-4 ring-1 ring-neutral-950/5 dark:ring-white/10">
        <DataTable<Row>
          data={rows}
          columns={columns}
          keyExtractor={(row) => row.id}
          tableClassName="[--gutter:theme(spacing.4)]"
          loading={loading}
        />
      </div>

      <p className="text-xs text-neutral-500 dark:text-neutral-400 m-0">
        {atCapacity
          ? `You can convert up to ${MAX_CONVERT_TO_PRESET_WORKSPACES} workspaces at once. Remove one to add another.`
          : `${selectedIds.length} of ${MAX_CONVERT_TO_PRESET_WORKSPACES} selected — add up to ${remaining} more.`}
      </p>
      {totalCount > results.length && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 m-0">
          Showing {results.length} of {totalCount} workspaces. Search to narrow the list.
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 m-0">
          Workspace search is unavailable. This workspace is still included.
        </p>
      )}
    </div>
  );
};

/**
 * Offers the files or templates this workspace actually used as preset defaults.
 * Both are already loaded on the workspace, so suggesting them costs no request.
 * Rendered even with nothing to offer — hiding it made the feature look absent.
 * Slotted as a description so Field's control -> description rule supplies the gap
 * above it; the pickers it follows are data-slot="control".
 */
const SuggestButton = ({
  count,
  label,
  emptyLabel,
  onClick
}: {
  count: number;
  label: string;
  emptyLabel: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={count === 0}
    data-slot="description"
    // Neutral, not sky: this fills a field in rather than advancing the wizard, so
    // carrying the same colour as the footer's primary action overstates it. The
    // icon and the hover shift carry the affordance instead.
    className="inline-flex items-center gap-1.5 text-sm/6 italic text-neutral-700 dark:text-neutral-300 enabled:hover:text-neutral-950 dark:enabled:hover:text-white disabled:text-neutral-400 dark:disabled:text-neutral-500 disabled:cursor-default transition-colors duration-150"
  >
    <SparklesIcon className="size-4 shrink-0" aria-hidden />
    {count === 0 ? emptyLabel : `${label} (${count})`}
  </button>
);

const CloseDuringGenerationConfirm = ({
  onStay,
  onConfirmClose
}: {
  onStay: () => void;
  onConfirmClose: () => void;
}) => (
  <div className="flex flex-col items-center text-center gap-4 py-6">
    <p className="text-sm font-semibold text-neutral-900 dark:text-white m-0">
      Close while Cara finishes?
    </p>
    <p className="text-xs text-neutral-500 dark:text-neutral-400 m-0 max-w-sm">
      Generation continues in the background. Your preset will appear in Custom Presets marked
      &ldquo;Needs review&rdquo; when it&apos;s ready.
    </p>
    <div className="flex items-center gap-2">
      <Button type="button" color="white" onClick={onConfirmClose}>
        Close
      </Button>
      <Button type="button" color="sky" onClick={onStay}>
        Keep waiting
      </Button>
    </div>
  </div>
);

export type ConvertToPresetWizardProps = {
  workspace: CaraWorkspaceFieldsFragment;
  open: boolean;
  onClose: () => void;
};

/**
 * "Convert to Preset" wizard. Steps 1-4 collect into one reducer; step 5 runs
 * generation and then hands off to {@link ConvertToPresetCompletion}.
 *
 * Steps 1-4 stay mounted (hidden when inactive) because their child inputs own
 * state the reducer does not — the workspace and file search fields — which
 * unmounting would silently discard on back-nav.
 */
export const ConvertToPresetWizard = ({ workspace, open, onClose }: ConvertToPresetWizardProps) => {
  const validBasePresets = React.useMemo(() => USE_CASE_FAMILIES.map((m) => m.preset), []);
  const buildInitialState = React.useCallback(
    (): ConvertToPresetWizardState =>
      initialConvertToPresetWizardState({
        sourceWorkspaceId: workspace.id,
        basePreset: defaultBasePreset(workspace.preset, validBasePresets)
      }),
    [workspace.id, workspace.preset, validBasePresets]
  );

  const [state, dispatch] = React.useReducer(
    convertToPresetWizardReducer,
    undefined,
    buildInitialState
  );
  const [previewFile, setPreviewFile] = React.useState<PresetFileOption | null>(null);
  const prevOpenRef = React.useRef(false);

  const [generationInput, setGenerationInput] = React.useState<StartPresetGenerationInput | null>(
    null
  );
  const [generationStatus, setGenerationStatus] = React.useState<PresetGenerationStatus>('idle');
  const [completedPresetId, setCompletedPresetId] = React.useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = React.useState(false);

  React.useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    dispatch({ type: 'reset', state: buildInitialState() });
    setPreviewFile(null);
    setGenerationInput(null);
    setGenerationStatus('idle');
    setCompletedPresetId(null);
    setConfirmingClose(false);
  }, [open, buildInitialState]);

  const wordTemplates = useGetWordDocumentTemplatesQuery({
    skip: !open,
    fetchPolicy: 'cache-first'
  });
  const htmlTemplates = useGetHtmlDocumentTemplatesQuery({
    skip: !open,
    fetchPolicy: 'cache-first'
  });
  const customForms = useGetCustomFormsQuery({ skip: !open, fetchPolicy: 'cache-first' });

  const wordOptions = React.useMemo(
    () => wordTemplatePickerOptions(wordTemplates.data?.wordDocumentTemplates ?? []),
    [wordTemplates.data?.wordDocumentTemplates]
  );
  const htmlOptions = React.useMemo(
    () => htmlTemplatePickerOptions(htmlTemplates.data?.htmlDocumentTemplates ?? []),
    [htmlTemplates.data?.htmlDocumentTemplates]
  );
  const formOptions = React.useMemo(
    () => customFormPickerOptions(customForms.data?.customForms ?? []),
    [customForms.data?.customForms]
  );

  const addOutputTemplates = React.useCallback(
    (...templates: CaraAttachment[]) => dispatch({ type: 'addOutputTemplates', templates }),
    []
  );
  const removeOutputTemplate = React.useCallback(
    (id: string) => dispatch({ type: 'removeOutputTemplate', id }),
    []
  );

  const isGenerationStep = state.step === GENERATION_STEP;
  const isFinalInputStep = state.step === 4;
  const canProceed = canProceedFromStep(state);

  // Suggestions come from what this workspace actually used: its attached files,
  // and — via artifact provenance — the Word templates its deliverables came from.
  const suggestedFiles = React.useMemo<PresetFileOption[]>(
    () => (workspace.files ?? []).map((f) => ({ id: f.id, name: f.name, mimeType: f.mimeType })),
    [workspace.files]
  );

  // Artifact provenance names the template each deliverable came from; titles are
  // resolved from the already-loaded picker options so the chip reads correctly.
  const suggestedTemplates = React.useMemo(() => {
    const htmlIds = new Set<string>();
    const formIds = new Set<string>();
    for (const artifact of workspace.artifacts ?? []) {
      const html = artifact.document?.htmlDocumentTemplateId;
      if (html) htmlIds.add(html);
      const form = artifact.templateForm?.templateId ?? artifact.customForm?.customForm?.id;
      if (form) formIds.add(form);
    }
    return [
      ...htmlOptions
        .filter((o) => htmlIds.has(o.id))
        .map((o) => templateAttachment('html-template', o)),
      ...formOptions
        .filter((o) => formIds.has(o.id))
        .map((o) => templateAttachment('custom-form', o))
    ];
  }, [workspace.artifacts, htmlOptions, formOptions]);

  // Freeze the assembled input before advancing: mounting the generation step is
  // what starts the mutation, and retry re-sends this same object.
  const startGeneration = React.useCallback(() => {
    setGenerationInput(assembleStartPresetGenerationInput(state));
    dispatch({ type: 'next' });
  }, [state]);

  const handleGenerationCompleted = React.useCallback(
    (presetId: string) => setCompletedPresetId(presetId),
    []
  );

  // A confirm, never a hard block: if progress polling stalls mid-flight the
  // status never leaves in-flight, and a hard block would wedge the modal forever.
  // The confirm's own Close calls onClose directly for the same reason.
  const generationInFlight = isPresetGenerationInFlight(generationStatus);
  const handleClose = React.useCallback(() => {
    // Escape on a preview backs out of it rather than discarding the whole wizard.
    if (previewFile) {
      setPreviewFile(null);
      return;
    }
    if (generationInFlight) {
      setConfirmingClose((prev) => !prev);
      return;
    }
    onClose();
  }, [generationInFlight, onClose, previewFile]);

  // Drop the confirm on leaving the in-flight phase so it cannot resurface over
  // the failure view after a retry.
  React.useEffect(() => {
    if (!generationInFlight) {
      setConfirmingClose(false);
    }
  }, [generationInFlight]);

  if (open && completedPresetId) {
    return <ConvertToPresetCompletion presetId={completedPresetId} onClose={onClose} />;
  }

  return (
    <Dialog open={open} onClose={handleClose} size="4xl">
      <DialogTitle>Convert to preset</DialogTitle>
      <DialogBody>
        {previewFile && (
          <PresetFilePreview file={previewFile} onBack={() => setPreviewFile(null)} />
        )}
        {/* Hidden rather than unmounted while previewing, for the same reason the
            steps hide behind each other: the pickers own state the reducer does not. */}
        <div className={clsx(previewFile && 'hidden')}>
          <StepIndicator currentStep={state.step} />

          <div className={clsx(state.step !== 1 && 'hidden')}>
            <OrientationStep workspaceTitle={workspace.title || 'this workspace'} />
          </div>

          <div className={clsx(state.step !== 2 && 'hidden')}>
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Workspaces</Label>
                  <Description>
                    This workspace is included automatically. Add up to{' '}
                    {MAX_CONVERT_TO_PRESET_WORKSPACES - 1} more similar workspaces to help Cara
                    learn the pattern.
                  </Description>
                  <WorkspacePickerTable
                    open={open}
                    source={{ id: workspace.id, title: workspace.title || 'Untitled workspace' }}
                    selectedIds={state.workspaceIds}
                    onChange={(ids) => dispatch({ type: 'setWorkspaceIds', workspaceIds: ids })}
                  />
                </Field>
                <Field>
                  <Label>Base preset</Label>
                  <Select
                    value={state.basePreset ?? ''}
                    onChange={(e) =>
                      dispatch({
                        type: 'setBasePreset',
                        basePreset: e.currentTarget.value
                          ? (e.currentTarget.value as CaraWorkspacePreset)
                          : null
                      })
                    }
                  >
                    {/* No explicit selection maps to basePresetOverride: null,
                      which the backend treats as "detect from the source
                      workspaces" — so the option must say that, not "None". */}
                    <option value="">Auto-detect</option>
                    {USE_CASE_FAMILIES.map((m) => (
                      <option key={m.preset} value={m.preset}>
                        {m.title}
                      </option>
                    ))}
                  </Select>
                  {state.basePreset ? (
                    <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400 m-0">
                      This preset adds to {basePresetTitle(state.basePreset)}. It doesn&apos;t
                      override that preset&apos;s core behavior.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400 m-0">
                      Cara will pick one from the source workspaces if any fits.
                    </p>
                  )}
                </Field>
              </FieldGroup>
            </Fieldset>
          </div>

          <div className={clsx(state.step !== 3 && 'hidden')}>
            <Fieldset>
              <FieldGroup>
                {/* Field spaces label -> control and description -> control, but has
                  no control -> control rule; the suggested grid and the search
                  picker are both controls. */}
                <Field className="[&>[data-slot=control]+[data-slot=control]]:mt-3">
                  <Label>Default files</Label>
                  <Description>
                    Documents Cara reads for context on every run — guidelines, sample data,
                    reference policies.
                  </Description>
                  {/* Existing files only: StartPresetGenerationInput.fileIds takes
                      ids, so an upload (which has none until it lands) would be
                      silently dropped at generation. */}
                  <PresetFilePicker
                    suggestedFiles={suggestedFiles}
                    addedFiles={state.addedFiles}
                    selectedIds={state.selectedFileIds}
                    onAdd={(file) => dispatch({ type: 'addFile', file })}
                    onToggle={(fileId) => dispatch({ type: 'toggleFile', fileId })}
                    onPreview={setPreviewFile}
                  />
                </Field>
                <Field>
                  <Label>Output templates</Label>
                  <Description>
                    Saved Word docs, document templates, and PDF forms Cara fills in when generating
                    deliverables.
                  </Description>
                  <OutputTemplatePicker
                    attachments={state.outputTemplates}
                    onAdd={addOutputTemplates}
                    onRemove={removeOutputTemplate}
                    buttonLabel="Add template or form"
                    buttonAriaLabel="Add output template or form"
                    customFormPicker={{
                      options: formOptions,
                      loading: customForms.loading,
                      description: presetCustomFormPickerDescription
                    }}
                    wordTemplatePicker={{
                      options: wordOptions,
                      loading: wordTemplates.loading,
                      description: presetWordTemplatePickerDescription
                    }}
                    htmlTemplatePicker={{
                      options: htmlOptions,
                      loading: htmlTemplates.loading,
                      description: presetHTMLTemplatePickerDescription
                    }}
                  />
                  <SuggestButton
                    count={suggestedTemplates.length}
                    label="Suggest templates from this workspace"
                    emptyLabel="No templates used on this workspace to suggest"
                    onClick={() => addOutputTemplates(...suggestedTemplates)}
                  />
                </Field>
                <Field>
                  <Label>Expected output formats</Label>
                  <Description>The deliverables Cara should produce on every run.</Description>
                  <Textarea
                    value={state.expectedOutputFormats}
                    rows={3}
                    maxLength={MAX_OUTPUT_FORMATS_CHARS}
                    onChange={(e) =>
                      dispatch({ type: 'setExpectedOutputFormats', value: e.currentTarget.value })
                    }
                    placeholder="e.g. a branded PDF proposal and a short cover email"
                  />
                </Field>
              </FieldGroup>
            </Fieldset>
          </div>

          <div className={clsx(state.step !== 4 && 'hidden')}>
            <ConvertToPresetQuestionsStep
              state={state}
              dispatch={dispatch}
              active={state.step >= 4}
            />
          </div>

          {isGenerationStep && (
            <>
              {/* The generation step stays mounted (hidden) under the confirm —
                unmounting it would re-fire the
                start mutation when the user chooses to stay. */}
              <div className={clsx(confirmingClose && 'hidden')}>
                <ConvertToPresetGenerationStep
                  input={generationInput}
                  workspaceId={workspace.id}
                  onStatusChange={setGenerationStatus}
                  onCompleted={handleGenerationCompleted}
                  onBack={() => dispatch({ type: 'back' })}
                  onClose={onClose}
                />
              </div>
              {confirmingClose && (
                <CloseDuringGenerationConfirm
                  onStay={() => setConfirmingClose(false)}
                  onConfirmClose={onClose}
                />
              )}
            </>
          )}
        </div>
      </DialogBody>
      {/* The generation step owns its own controls (continue-in-tab, retry,
          close), so the wizard footer is hidden there. So does the preview. */}
      {!isGenerationStep && !previewFile && (
        <DialogActions>
          {state.step > 1 && (
            <Button type="button" color="white" onClick={() => dispatch({ type: 'back' })}>
              Back
            </Button>
          )}
          <Button type="button" color="white" onClick={handleClose}>
            Cancel
          </Button>
          {isFinalInputStep ? (
            <Button type="button" color="sky" onClick={startGeneration} disabled={!canProceed}>
              Generate preset
            </Button>
          ) : (
            <Button
              type="button"
              color="sky"
              onClick={() => dispatch({ type: 'next' })}
              disabled={!canProceed}
            >
              Next
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
